/**
 * Facebook Conversions API Client
 * Sends server-side events for improved tracking accuracy and match rates
 * 
 * Security: Auth is handled server-side via origin validation and rate limiting.
 * No client-side secrets are used - the edge function validates the request origin.
 */

import { supabase } from '@/integrations/supabase/client';

interface UserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  externalId?: string;
}

interface CustomData {
  value?: number;
  currency?: string;
  contentName?: string;
  contentCategory?: string;
  contentIds?: string[];
  contentType?: string;
  status?: string;
}

interface ConversionEvent {
  eventName: string;
  eventSourceUrl?: string;
  userData?: UserData;
  customData?: CustomData;
  eventId?: string;
}

// Get Facebook cookies for better matching
function getFacebookCookies(): { fbc?: string; fbp?: string } {
  const cookies: { fbc?: string; fbp?: string } = {};
  
  try {
    const cookieString = document.cookie;
    const fbcMatch = cookieString.match(/_fbc=([^;]+)/);
    const fbpMatch = cookieString.match(/_fbp=([^;]+)/);
    
    if (fbcMatch) cookies.fbc = fbcMatch[1];
    if (fbpMatch) cookies.fbp = fbpMatch[1];
  } catch (e) {
    // Silently handle cookie read errors
  }
  
  return cookies;
}

/**
 * Send events to Facebook Conversions API via edge function
 * Security: Uses Supabase SDK which handles auth via origin validation
 */
async function sendEvents(events: ConversionEvent[]): Promise<boolean> {
  try {
    const { fbc, fbp } = getFacebookCookies();
    
    // Add Facebook cookies and current URL to each event
    const enrichedEvents = events.map(event => ({
      ...event,
      eventSourceUrl: event.eventSourceUrl || window.location.href,
      userData: {
        ...event.userData,
        fbc,
        fbp,
        clientUserAgent: navigator.userAgent,
      },
    }));

    // Use Supabase SDK - auth handled via origin validation server-side
    const { data, error } = await supabase.functions.invoke('meta-conversions-api', {
      body: { events: enrichedEvents },
    });

    if (error) {
      console.error('CAPI error:', error.message);
      return false;
    }

    console.log('CAPI success:', data);
    return true;
  } catch (e) {
    console.error('Failed to send CAPI events:', e);
    return false;
  }
}

/**
 * Pre-built CAPI event senders
 * All methods support eventId for deduplication with browser pixel
 */
export const metaCAPI = {
  /**
   * Track a Lead event (form submission)
   */
  lead: async (params: {
    email?: string;
    phone?: string;
    firstName?: string;
    contentName?: string;
    contentCategory?: string;
    eventId?: string;
  }) => {
    const nameParts = params.firstName?.split(' ') || [];
    
    return sendEvents([{
      eventName: 'Lead',
      eventId: params.eventId,
      userData: {
        email: params.email,
        phone: params.phone,
        firstName: nameParts[0],
        lastName: nameParts.slice(1).join(' ') || undefined,
      },
      customData: {
        contentName: params.contentName,
        contentCategory: params.contentCategory || 'real_estate',
      },
    }]);
  },

  /**
   * Track a CompleteRegistration event
   */
  completeRegistration: async (params: {
    email?: string;
    phone?: string;
    firstName?: string;
    status?: string;
    eventId?: string;
  }) => {
    const nameParts = params.firstName?.split(' ') || [];
    
    return sendEvents([{
      eventName: 'CompleteRegistration',
      eventId: params.eventId,
      userData: {
        email: params.email,
        phone: params.phone,
        firstName: nameParts[0],
        lastName: nameParts.slice(1).join(' ') || undefined,
      },
      customData: {
        status: params.status || 'registered',
      },
    }]);
  },

  /**
   * Track a Contact event (WhatsApp click, call request)
   */
  contact: async (params?: { contentName?: string; eventId?: string }) => {
    return sendEvents([{
      eventName: 'Contact',
      eventId: params?.eventId,
      customData: {
        contentName: params?.contentName,
      },
    }]);
  },

  /**
   * Track a ViewContent event
   */
  viewContent: async (params: {
    contentName?: string;
    contentCategory?: string;
    contentIds?: string[];
    contentType?: string;
    eventId?: string;
  }) => {
    const { eventId, ...customData } = params;
    return sendEvents([{
      eventName: 'ViewContent',
      eventId,
      customData,
    }]);
  },

  /**
   * Track a custom event
   */
  customEvent: async (
    eventName: string,
    userData?: UserData,
    customData?: CustomData,
    eventId?: string
  ) => {
    return sendEvents([{
      eventName,
      eventId,
      userData,
      customData,
    }]);
  },
};
